/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HSS BALLISTICS → OVERWATCH TRAINER EXPORT MODULE
 * Version: 2.0.0-SECURE
 * 
 * REQUIRES: HSS_BALLISTICS_v7.6.3_SECURE.js (must load first)
 * 
 * SHOPIFY INSTALLATION:
 * 1. Upload this file to: Settings → Files
 * 2. Add to your Liquid template AFTER the main ballistics script:
 *    <script src="{{ 'BALLISTICS_EXPORT_MODULE_v2.js' | asset_url }}" defer></script>
 * 
 * FEATURES:
 * - Integrates with HSS_BALLISTICS_v7.6.3 data structures
 * - Domain-locked security
 * - Uses window.hssExportData and window.trajectoryArray from main calculator
 * - Enhanced status indicator showing export readiness
 * - 24-hour localStorage persistence
 * ═══════════════════════════════════════════════════════════════════════════
 * @copyright Wizhunt Inc / SWAT Optics - All Rights Reserved
 */

(function() {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════════════
  // SECURITY: Domain Authorization
  // ═══════════════════════════════════════════════════════════════════════════
  const AUTHORIZED_DOMAINS = [
    'swatoptics.com',
    'www.swatoptics.com',
    'localhost',
    '127.0.0.1'
  ];

  function isAuthorizedDomain() {
    const hostname = window.location.hostname.toLowerCase();
    return AUTHORIZED_DOMAINS.some(function(domain) {
      return hostname === domain || 
             hostname.endsWith('.' + domain) || 
             hostname.endsWith('.myshopify.com');
    });
  }

  if (!isAuthorizedDomain()) {
    console.error('HSS Export Module: Unauthorized domain');
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════
  
  var OVERWATCH_CONFIG = Object.freeze({
    storageKey: 'hss_dope_data',
    version: '2.0.0',
    overwatchUrl: '/pages/overwatch-trainer',
    expirationMs: 86400000 // 24 hours
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // EXPORT FUNCTION - Main export logic
  // ═══════════════════════════════════════════════════════════════════════════
  
  function exportToOverwatch() {
    // Method 1: Use the structured export data from HSS_BALLISTICS_v7.6.3
    if (window.hssExportData && window.hssExportData.data && window.hssExportData.data.length > 0) {
      return exportFromHssData(window.hssExportData);
    }
    
    // Method 2: Use trajectoryArray if available
    if (window.trajectoryArray && window.trajectoryArray.length > 0) {
      return exportFromTrajectoryArray(window.trajectoryArray);
    }
    
    // Method 3: Fallback to DOM parsing
    var parsed = parseResultsFromDOM();
    if (parsed && parsed.ranges.length >= 3) {
      return exportFromParsedData(parsed);
    }
    
    // No data found
    showExportMessage('⚠️ Please calculate your ballistics first!', 'error');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EXPORT FROM HSS_BALLISTICS_v7.6.3 DATA STRUCTURES
  // ═══════════════════════════════════════════════════════════════════════════
  
  function exportFromHssData(hssData) {
    var dopeData = {
      version: OVERWATCH_CONFIG.version,
      timestamp: Date.now(),
      source: 'HSS Ballistics Calculator v7.6.3',
      
      // Rifle/Load info from hssExportData
      caliber: hssData.config ? hssData.config.caliber : 'Unknown',
      load: hssData.config ? hssData.config.ammunition : 'Unknown Load',
      barrelLength: hssData.config ? parseFloat(hssData.config.barrelLength) : 16,
      zeroRange: hssData.config ? parseInt(hssData.config.zeroRange) : 100,
      scopeType: hssData.scopeType || '556',
      scopeName: hssData.scopeName || 'HSS DMR',
      
      // Atmospheric conditions
      conditions: {
        temperature: hssData.config ? parseFloat(hssData.config.temperature) : 59,
        elevation: hssData.config ? parseFloat(hssData.config.elevation) : 0,
        humidity: hssData.config ? parseFloat(hssData.config.humidity) : 50,
        windSpeed: hssData.config ? parseFloat(hssData.config.windSpeed) : 0,
        windAngle: hssData.config ? parseFloat(hssData.config.windAngle) : 90
      },
      
      // Ballistic tables
      dropTable: {},
      dropInches: {},
      windTable: {},
      bdcHolds: {},
      milTable: {},
      velocityTable: {},
      energyTable: {}
    };
    
    // Populate from hssExportData.data array
    hssData.data.forEach(function(row) {
      var range = row.range;
      if (!range || range < 25 || range > 2000) return;
      
      dopeData.dropTable[range] = row.dropMOA || row.moa || 0;
      dopeData.dropInches[range] = row.dropInches || row.drop || 0;
      dopeData.milTable[range] = row.dropMils || row.mil || (dopeData.dropTable[range] / 3.438);
      dopeData.windTable[range] = row.windMOA || row.wind || 0;
      
      if (row.bdcMark !== undefined) {
        dopeData.bdcHolds[range] = row.bdcMark;
      }
      if (row.velocity) {
        dopeData.velocityTable[range] = row.velocity;
      }
      if (row.energy) {
        dopeData.energyTable[range] = row.energy;
      }
    });
    
    return saveAndShowSuccess(dopeData);
  }
  
  function exportFromTrajectoryArray(trajectory) {
    // Get config from form inputs
    var caliber = getInputValue('caliber') || 'Unknown';
    var load = getSelectText('ammunitionLoad') || 'Unknown Load';
    var zeroRange = getInputValue('zeroDistance') || 100;
    var barrelLength = getInputValue('barrelLength') || 16;
    var scopeType = getInputValue('scopeType') || '556';
    
    var dopeData = {
      version: OVERWATCH_CONFIG.version,
      timestamp: Date.now(),
      source: 'HSS Ballistics Calculator v7.6.3',
      
      caliber: caliber,
      load: load,
      barrelLength: parseFloat(barrelLength),
      zeroRange: parseInt(zeroRange),
      scopeType: scopeType,
      
      conditions: {
        temperature: parseFloat(getInputValue('temperature')) || 59,
        elevation: parseFloat(getInputValue('elevation')) || 0,
        humidity: parseFloat(getInputValue('humidity')) || 50,
        windSpeed: parseFloat(getInputValue('windSpeed')) || 0,
        windAngle: parseFloat(getInputValue('windAngle')) || 90
      },
      
      dropTable: {},
      dropInches: {},
      windTable: {},
      bdcHolds: {},
      milTable: {},
      velocityTable: {},
      energyTable: {}
    };
    
    trajectory.forEach(function(point) {
      var range = point.range;
      if (!range || range < 25 || range > 2000) return;
      
      // Calculate MOA from drop in inches
      var dropInches = point.drop || 0;
      var dropMOA = (Math.abs(dropInches) / range) * 95.5; // Convert inches to MOA
      if (dropInches < 0) dropMOA = -dropMOA;
      
      dopeData.dropInches[range] = dropInches;
      dopeData.dropTable[range] = point.dropMOA || dropMOA;
      dopeData.milTable[range] = point.dropMils || (dropMOA / 3.438);
      dopeData.windTable[range] = point.windDrift || point.windMOA || 0;
      
      if (point.velocity) dopeData.velocityTable[range] = point.velocity;
      if (point.energy) dopeData.energyTable[range] = point.energy;
    });
    
    return saveAndShowSuccess(dopeData);
  }
  
  function exportFromParsedData(parsed) {
    var dopeData = {
      version: OVERWATCH_CONFIG.version,
      timestamp: Date.now(),
      source: 'HSS Ballistics Calculator',
      
      caliber: getInputValue('caliber') || 'Custom',
      load: getSelectText('ammunitionLoad') || 'Custom Load',
      barrelLength: parseFloat(getInputValue('barrelLength')) || 16,
      zeroRange: parseInt(getInputValue('zeroDistance')) || 100,
      scopeType: getInputValue('scopeType') || 'generic',
      
      conditions: {
        temperature: parseFloat(getInputValue('temperature')) || 59,
        elevation: parseFloat(getInputValue('elevation')) || 0,
        humidity: parseFloat(getInputValue('humidity')) || 50,
        windSpeed: parseFloat(getInputValue('windSpeed')) || 0,
        windAngle: parseFloat(getInputValue('windAngle')) || 90
      },
      
      dropTable: {},
      dropInches: {},
      windTable: {},
      bdcHolds: {},
      milTable: {}
    };
    
    parsed.ranges.forEach(function(range, i) {
      dopeData.dropTable[range] = parsed.dropMOA[i] || 0;
      dopeData.dropInches[range] = parsed.dropInches[i] || 0;
      dopeData.milTable[range] = parsed.dropMils[i] || (parsed.dropMOA[i] / 3.438);
      dopeData.windTable[range] = parsed.windMOA[i] || 0;
      if (parsed.bdcHolds[i] !== null) {
        dopeData.bdcHolds[range] = parsed.bdcHolds[i];
      }
    });
    
    return saveAndShowSuccess(dopeData);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DOM PARSING FALLBACK
  // ═══════════════════════════════════════════════════════════════════════════
  
  function parseResultsFromDOM() {
    var result = {
      ranges: [],
      dropMOA: [],
      dropInches: [],
      dropMils: [],
      windMOA: [],
      bdcHolds: []
    };
    
    // Look for trajectory table rows
    var rows = document.querySelectorAll('#trajectoryResults tr, .trajectory-table tr, [data-range]');
    
    rows.forEach(function(row) {
      if (row.querySelector('th')) return; // Skip headers
      
      var cells = row.querySelectorAll('td');
      if (cells.length < 2) return;
      
      // Extract range from first column
      var rangeText = cells[0] ? cells[0].textContent : '';
      var rangeMatch = rangeText.match(/(\d{2,4})/);
      if (!rangeMatch) return;
      
      var range = parseInt(rangeMatch[1]);
      if (range < 25 || range > 2000) return;
      
      // Look for numeric values in cells
      var values = [];
      for (var i = 1; i < cells.length; i++) {
        var text = cells[i].textContent.trim();
        var numMatch = text.match(/(-?[\d.]+)/);
        if (numMatch) {
          values.push(parseFloat(numMatch[1]));
        }
      }
      
      if (values.length >= 1) {
        result.ranges.push(range);
        result.dropInches.push(values[0] || 0);
        result.dropMOA.push(values[1] || 0);
        result.dropMils.push(values[2] || null);
        result.windMOA.push(values[3] || null);
        result.bdcHolds.push(null);
      }
    });
    
    return result;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SAVE AND SUCCESS HANDLING
  // ═══════════════════════════════════════════════════════════════════════════
  
  function saveAndShowSuccess(dopeData) {
    try {
      localStorage.setItem(OVERWATCH_CONFIG.storageKey, JSON.stringify(dopeData));
      
      console.log('═══ DOPE EXPORTED TO OVERWATCH ═══');
      console.log('Caliber:', dopeData.caliber);
      console.log('Load:', dopeData.load);
      console.log('Ranges:', Object.keys(dopeData.dropTable).sort(function(a,b) { return a-b; }).join(', '));
      
      showExportSuccess(dopeData);
      return true;
      
    } catch (e) {
      console.error('Failed to save DOPE data:', e);
      showExportMessage('⚠️ Failed to save data. Check browser storage settings.', 'error');
      return false;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UI HELPERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  function getInputValue(id) {
    var el = document.getElementById(id);
    return el ? el.value : null;
  }
  
  function getSelectText(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    var selected = el.options[el.selectedIndex];
    return selected ? selected.text : el.value;
  }
  
  function showExportMessage(message, type) {
    var existing = document.getElementById('hss-export-message');
    if (existing) existing.remove();
    
    var div = document.createElement('div');
    div.id = 'hss-export-message';
    div.style.cssText = 
      'position: fixed; top: 20px; left: 50%; transform: translateX(-50%);' +
      'padding: 15px 25px; border-radius: 8px; font-weight: 600; font-size: 14px;' +
      'z-index: 10000; box-shadow: 0 4px 20px rgba(0,0,0,0.3);' +
      'animation: hssSlideDown 0.3s ease;' +
      (type === 'error' ? 'background: #dc2626; color: white;' : 
       type === 'success' ? 'background: #16a34a; color: white;' : 
       'background: #2563eb; color: white;');
    div.textContent = message;
    document.body.appendChild(div);
    
    setTimeout(function() { div.remove(); }, 4000);
  }
  
  function showExportSuccess(dopeData) {
    var existing = document.getElementById('hss-export-modal');
    if (existing) existing.remove();
    
    var rangeList = Object.keys(dopeData.dropTable).sort(function(a,b) { return parseInt(a) - parseInt(b); });
    var minRange = rangeList[0];
    var maxRange = rangeList[rangeList.length - 1];
    
    var modal = document.createElement('div');
    modal.id = 'hss-export-modal';
    modal.innerHTML = 
      '<div style="' +
        'position: fixed; top: 0; left: 0; right: 0; bottom: 0;' +
        'background: rgba(0,0,0,0.7); display: flex; align-items: center;' +
        'justify-content: center; z-index: 10001;' +
      '" onclick="this.parentElement.remove()">' +
        '<div style="' +
          'background: linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%);' +
          'border: 2px solid #4ade80; border-radius: 12px; padding: 25px 35px;' +
          'max-width: 400px; text-align: center; color: #eee;' +
          'box-shadow: 0 10px 40px rgba(0,0,0,0.5);' +
        '" onclick="event.stopPropagation()">' +
          '<div style="font-size: 40px; margin-bottom: 10px;">✅</div>' +
          '<h3 style="color: #4ade80; margin: 0 0 15px 0; font-size: 18px;">DOPE EXPORTED!</h3>' +
          
          '<div style="' +
            'background: rgba(74, 222, 128, 0.1); border: 1px solid #4ade80;' +
            'border-radius: 8px; padding: 12px; margin-bottom: 20px;' +
            'font-size: 13px; text-align: left;' +
          '">' +
            '<div style="color: #888; margin-bottom: 5px;">Saved:</div>' +
            '<div><strong>' + escapeHtml(dopeData.caliber) + '</strong> - ' + escapeHtml(dopeData.load) + '</div>' +
            '<div style="color: #4ade80; margin-top: 5px;">' +
              rangeList.length + ' ranges (' + minRange + '-' + maxRange + ' yards)' +
            '</div>' +
            '<div style="color: #888; margin-top: 5px; font-size: 11px;">' +
              'Scope: ' + escapeHtml(dopeData.scopeName || dopeData.scopeType || 'Standard') +
            '</div>' +
          '</div>' +
          
          '<button id="hss-open-overwatch-btn" style="' +
            'background: #4ade80; color: #000; border: none; padding: 12px 30px;' +
            'border-radius: 6px; font-weight: 700; font-size: 14px; cursor: pointer;' +
            'margin-right: 10px; transition: all 0.2s; min-height: 44px;' +
          '">🎯 OPEN OVERWATCH</button>' +
          
          '<button id="hss-close-modal-btn" style="' +
            'background: transparent; color: #888; border: 1px solid #444;' +
            'padding: 12px 20px; border-radius: 6px; font-weight: 600;' +
            'font-size: 14px; cursor: pointer; transition: all 0.2s; min-height: 44px;' +
          '">Close</button>' +
          
          '<div style="margin-top: 15px; font-size: 11px; color: #666;">' +
            'Data saved to browser. Valid for 24 hours.' +
          '</div>' +
        '</div>' +
      '</div>';
    
    document.body.appendChild(modal);
    
    // Add event listeners (safer than inline onclick)
    document.getElementById('hss-open-overwatch-btn').addEventListener('click', function() {
      window.open(OVERWATCH_CONFIG.overwatchUrl, '_blank');
      modal.remove();
    });
    
    document.getElementById('hss-close-modal-btn').addEventListener('click', function() {
      modal.remove();
    });
  }
  
  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STATUS INDICATOR - Shows when data is ready to export
  // ═══════════════════════════════════════════════════════════════════════════
  
  function updateExportStatus() {
    var statusDiv = document.getElementById('hss-export-status');
    if (!statusDiv) return;
    
    var hasData = (window.hssExportData && window.hssExportData.data && window.hssExportData.data.length > 0) ||
                  (window.trajectoryArray && window.trajectoryArray.length > 0);
    
    if (hasData) {
      var data = window.hssExportData || {};
      var config = data.config || {};
      var rangeCount = data.data ? data.data.length : (window.trajectoryArray ? window.trajectoryArray.length : 0);
      
      var caliber = config.caliber || getSelectText('caliber') || 'Unknown';
      var minRange = data.data && data.data[0] ? data.data[0].range : 50;
      var maxRange = data.data && data.data[data.data.length-1] ? data.data[data.data.length-1].range : 800;
      
      statusDiv.innerHTML = 
        '<div style="background: rgba(74, 222, 128, 0.15); border: 1px solid #4ade80; ' +
        'border-radius: 8px; padding: 10px 15px; display: flex; align-items: center; gap: 10px;">' +
          '<span style="color: #4ade80; font-size: 16px;">✓</span>' +
          '<span style="color: #4ade80; font-weight: 600;">Ready to export: </span>' +
          '<span style="color: #e2e8f0;">' + escapeHtml(caliber) + ' (' + rangeCount + ' loads) • ' + 
          rangeCount + ' ranges (' + minRange + '-' + maxRange + 'y)</span>' +
        '</div>';
    } else {
      statusDiv.innerHTML = 
        '<div style="background: rgba(100, 116, 139, 0.15); border: 1px solid #64748b; ' +
        'border-radius: 8px; padding: 10px 15px; color: #94a3b8;">' +
          '💡 Calculate your ballistics first, then export to Overwatch Trainer' +
        '</div>';
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ADD EXPORT UI TO PAGE
  // ═══════════════════════════════════════════════════════════════════════════
  
  function addExportUI() {
    // Don't add if already exists
    if (document.getElementById('hss-overwatch-export-section')) return;
    
    // Find the export section or results area
    var container = document.getElementById('exportSection') ||
                    document.getElementById('trajectoryResults') ||
                    document.querySelector('.results-section') ||
                    document.getElementById('results');
    
    if (!container) {
      console.warn('HSS Export: Could not find container for export UI');
      return;
    }
    
    // Create export section
    var section = document.createElement('div');
    section.id = 'hss-overwatch-export-section';
    section.style.cssText = 'margin-top: 1.5rem; text-align: center;';
    
    section.innerHTML = 
      '<div id="hss-export-status" style="margin-bottom: 1rem;"></div>' +
      '<button id="hss-overwatch-export-btn" style="' +
        'background: linear-gradient(180deg, #4ade80 0%, #22c55e 100%);' +
        'color: #000; border: none; padding: 14px 28px; border-radius: 8px;' +
        'font-weight: 700; font-size: 15px; cursor: pointer;' +
        'transition: all 0.2s; box-shadow: 0 4px 12px rgba(74, 222, 128, 0.3);' +
        'min-height: 52px; -webkit-tap-highlight-color: transparent;' +
      '">🎯 Export to Overwatch Trainer</button>';
    
    container.appendChild(section);
    
    // Add click handler
    document.getElementById('hss-overwatch-export-btn').addEventListener('click', exportToOverwatch);
    
    // Add hover effects
    var btn = document.getElementById('hss-overwatch-export-btn');
    btn.addEventListener('mouseover', function() {
      btn.style.transform = 'translateY(-2px)';
      btn.style.boxShadow = '0 6px 16px rgba(74, 222, 128, 0.4)';
    });
    btn.addEventListener('mouseout', function() {
      btn.style.transform = 'translateY(0)';
      btn.style.boxShadow = '0 4px 12px rgba(74, 222, 128, 0.3)';
    });
    
    // Initial status update
    updateExportStatus();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // WATCH FOR CALCULATION UPDATES
  // ═══════════════════════════════════════════════════════════════════════════
  
  function watchForUpdates() {
    // Update status when trajectoryResults changes
    var resultsDiv = document.getElementById('trajectoryResults');
    if (resultsDiv) {
      var observer = new MutationObserver(function() {
        setTimeout(updateExportStatus, 100);
      });
      observer.observe(resultsDiv, { childList: true, subtree: true });
    }
    
    // Also update on calculate button click
    var calcBtn = document.getElementById('calculateTrajectory');
    if (calcBtn) {
      calcBtn.addEventListener('click', function() {
        setTimeout(updateExportStatus, 600);
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZE
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Add CSS animation
  var style = document.createElement('style');
  style.textContent = 
    '@keyframes hssSlideDown {' +
      'from { opacity: 0; transform: translateX(-50%) translateY(-20px); }' +
      'to { opacity: 1; transform: translateX(-50%) translateY(0); }' +
    '}';
  document.head.appendChild(style);
  
  function initExportModule() {
    // Wait a bit for main calculator to initialize
    setTimeout(function() {
      addExportUI();
      watchForUpdates();
      console.log('✓ HSS Overwatch Export Module v2.0.0 loaded');
    }, 500);
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExportModule);
  } else {
    initExportModule();
  }
  
  // Expose functions globally
  window.exportToOverwatch = exportToOverwatch;
  window.updateExportStatus = updateExportStatus;
  window.HSS_EXPORT = Object.freeze({
    export: exportToOverwatch,
    updateStatus: updateExportStatus,
    config: OVERWATCH_CONFIG,
    version: '2.0.0-SECURE'
  });
  
})();